import { splitPageSectionHref } from './linkTargets';

const PLACEHOLDER_LINK_TEXT = new Set(['new link', 'new sub link']);

export interface MenuLinkPage {
  title: string;
  slug: string;
  isHomePage?: boolean;
}

export function isPlaceholderMenuLinkText(text: string | undefined): boolean {
  return PLACEHOLDER_LINK_TEXT.has((text ?? '').trim().toLowerCase());
}

/** Page title for an internal menu URL. External and section-only URLs return null. */
export function pageTitleForMenuUrl(url: string, pages: MenuLinkPage[]): string | null {
  const trimmed = (url ?? '').trim();
  if (
    !trimmed ||
    trimmed.startsWith('#') ||
    trimmed.startsWith('//') ||
    /^https?:/i.test(trimmed) ||
    /^(mailto:|tel:|javascript:)/i.test(trimmed)
  ) {
    return null;
  }

  const { basePath } = splitPageSectionHref(trimmed);
  if (basePath === '/' || basePath === '') {
    const home = pages.find(page => page.isHomePage);
    return home?.title?.trim() || 'Home';
  }

  const slug = basePath.replace(/^\/+|\/+$/g, '');
  const page = pages.find(item => item.slug === slug);
  return page?.title?.trim() || null;
}

/**
 * When the destination page changes, replace a placeholder or auto-filled label
 * with that page's title. Custom labels and section-only changes stay put.
 */
export function menuLinkUpdateForUrl(
  currentText: string,
  previousUrl: string,
  nextUrl: string,
  pages: MenuLinkPage[]
): { url: string; text?: string } {
  const previousBase = splitPageSectionHref(previousUrl || '').basePath;
  const nextBase = splitPageSectionHref(nextUrl || '').basePath;
  if (previousBase === nextBase) return { url: nextUrl };

  const nextTitle = pageTitleForMenuUrl(nextUrl, pages);
  if (!nextTitle) return { url: nextUrl };

  const previousTitle = pageTitleForMenuUrl(previousUrl, pages);
  const followsPageName =
    isPlaceholderMenuLinkText(currentText) ||
    !currentText.trim() ||
    (previousTitle != null && currentText === previousTitle);

  if (!followsPageName) return { url: nextUrl };
  return { url: nextUrl, text: nextTitle };
}

/** Shown label when a saved link still has the placeholder text. */
export function visibleMenuLinkText(text: string, url: string): string {
  if (!isPlaceholderMenuLinkText(text)) return text;

  const { basePath } = splitPageSectionHref((url ?? '').trim());
  if (!basePath || basePath === '/') return 'Home';

  const slug = basePath.split('/').filter(Boolean).pop() || '';
  if (!slug) return text;

  return slug
    .split('-')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
